import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { type RatesByType, type PriceCode, getRateForPriceCode } from './RateCalculator';
import { useLocation } from 'react-router-dom';
import FacilitiesModal, { type FacilitiesData } from './FacilitiesModal';

interface PriceCodeType extends PriceCode {}

interface StayHistoryEntry {
  year: number;
  guestName: string;
  duration?: string;
  registrationId?: string;
  startDate?: string;
  endDate?: string;
}

interface PrivateCottageInfo {
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerAddress: string;
}

interface FunderInfo {
  funderName: string;
  funderEmail: string;
  funderPhone: string;
  funderAddress: string;
}

interface DonorInfo {
  donorName: string;
  donorEmail: string;
  donorPhone: string;
  donorAddress: string;
}

interface Accommodation {
  id: string;
  placeCode: string;
  description: string;
  status: 'available' | 'occupied' | 'maintenance';
  priceCode: string;
  facilities: string[];
  stayHistory: StayHistoryEntry[];
  photoUrl?: string;
  accommodationType: 'Standard' | 'Private' | 'Staff' | 'Funded' | 'Donated';
  privateCottageInfo?: PrivateCottageInfo;
  funderInfo?: FunderInfo;
  donorInfo?: DonorInfo;
  notes: string;
  location: string;
  capacity: number;
  placeType: keyof InventoryState;
  placeName: string;
  placeNumber: string;
  facilitiesData?: FacilitiesData;
}

interface InventoryState {
  Cottage: Accommodation[];
  Apartment: Accommodation[];
  dormA: Accommodation[];
  dormB: Accommodation[];
  Fireside: Accommodation[];
  LowerCourt: Accommodation[];
  UpperCourt: Accommodation[];
  privateCottage: Accommodation[];
}

type TypeFilterSelection = keyof InventoryState | 'Funded' | 'Maintenance';

const TYPE_FILTER_LABELS: Record<TypeFilterSelection, string> = {
  Cottage: 'Cottage',
  Apartment: 'Apartment',
  dormA: 'Dorm A',
  dormB: 'Dorm B',
  Fireside: 'Fireside',
  LowerCourt: 'Lower Court',
  UpperCourt: 'Upper Court',
  privateCottage: 'Private Cottage',
  Funded: 'Funded',
  Maintenance: 'Maintenance'
};

const PRICE_CODES: PriceCodeType[] = [
  // Cottages
  { code: 'C1', description: 'Cottage (Full Service)', type: 'cottages', serviceLevel: 'full' },
  { code: 'C2', description: 'Cottage (Partial Service)', type: 'cottages', serviceLevel: 'partial' },
  { code: 'C3', description: 'Cottage (No Service)', type: 'cottages', serviceLevel: 'none' },
  { code: 'P1', description: 'Private Cottage (Full Service)', type: 'cottages', serviceLevel: 'full' },
  { code: 'P2', description: 'Private Cottage (Partial Service)', type: 'cottages', serviceLevel: 'partial' },
  { code: 'P3', description: 'Private Cottage (No Service)', type: 'cottages', serviceLevel: 'none' },
  
  // Apartments
  { code: 'A1', description: 'Apartment (Full Service)', type: 'apartments', serviceLevel: 'full' },
  { code: 'A2', description: 'Apartment (Partial Service)', type: 'apartments', serviceLevel: 'partial' },
  { code: 'A3', description: 'Apartment (No Service)', type: 'apartments', serviceLevel: 'none' },
  
  // Dorms
  { code: 'D1', description: 'Dorm (Single)', type: 'dorm', serviceLevel: 'single' },
  { code: 'D2', description: 'Dorm (Double)', type: 'dorm', serviceLevel: 'double' },
  
  // Camp Sites
  { code: 'T1', description: 'Camp Site (Full Service)', type: 'campsite', serviceLevel: 'full' },
  { code: 'T2', description: 'Camp Site (Partial Service)', type: 'campsite', serviceLevel: 'partial' }
];

const FACILITIES = [
  'Air Conditioning',
  'Private Bathroom',
  'Kitchen',
  'Refrigerator',
  'Microwave',
  'WiFi',
  'Parking'
] as const;

type InventoryToRateType = {
  Cottage: 'cottages';
  Apartment: 'apartments';
  dormA: 'dorm';
  dormB: 'dorm';
  Fireside: 'dorm';
  LowerCourt: 'campsite';
  UpperCourt: 'campsite';
  privateCottage: 'cottages';
};

const getAccommodationType = (type: keyof InventoryState): keyof RatesByType => {
  const typeMap: InventoryToRateType = {
    Cottage: 'cottages',
    Apartment: 'apartments',
    dormA: 'dorm',
    dormB: 'dorm',
    Fireside: 'dorm',
    LowerCourt: 'campsite',
    UpperCourt: 'campsite',
    privateCottage: 'cottages'
  };
  return typeMap[type];
};

const InventoryManagement: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'byType' | 'unassignedByType' | 'unassignedByPriceCode' | 'byAccommodationType'>('all');
  const [selectedType, setSelectedType] = useState<TypeFilterSelection | null>(null);
  const [selectedPriceCode, setSelectedPriceCode] = useState<string | null>(null);
  const [selectedAccommodationType, setSelectedAccommodationType] = useState<string | null>(null);
  const [placeCodeSearch, setPlaceCodeSearch] = useState('');
  const [inventory, setInventory] = useState<InventoryState>({
    Cottage: [],
    Apartment: [],
    dormA: [],
    dormB: [],
    Fireside: [],
    LowerCourt: [],
    UpperCourt: [],
    privateCottage: []
  });
  const [annualRates, setAnnualRates] = useState<RatesByType | null>(null);
  const [settings, setSettings] = useState<any>(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedAccommodation, setSelectedAccommodation] = useState<Accommodation | null>(null);
  const [showFacilitiesModal, setShowFacilitiesModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [editSnapshot, setEditSnapshot] = useState<Accommodation | null>(null);
  const [pendingDonateFrom, setPendingDonateFrom] = useState<'privateCottage' | null>(null);
  const [showDonatePricePicker, setShowDonatePricePicker] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load inventory and rates separately to handle errors independently
        await fetchInventory();
        try {
          await fetchAnnualRates();
        } catch (err) {
          // Rate loading errors shouldn't prevent inventory from showing
          console.error('Error loading rates:', err);
        }
      } catch (err) {
        console.error('Error loading inventory:', err);
        setError('Failed to load inventory data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []); // Remove location.search dependency

  // Separate useEffect for handling URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const placeCode = params.get('placeCode');
    const shouldSelect = params.get('select') === 'true';
    
    if (placeCode && shouldSelect && !loading) {
      // Search through all categories
      for (const type of Object.keys(inventory) as Array<keyof InventoryState>) {
        const found = inventory[type].find(room => 
          room.placeCode.toLowerCase() === placeCode.toLowerCase()
        );
        if (found) {
          setSelectedAccommodation(found);
          setEditSnapshot(found);
          setPendingDonateFrom(null);
          setShowDonatePricePicker(false);
          setEditMode(true);
          return;
        }
      }
      // If not found, show an error message
      setError(`No accommodation found with Place Code: ${placeCode}`);
      setTimeout(() => setError(''), 3000);
    }
  }, [location.search, inventory, loading]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'camp');
        const docSnap = await getDoc(settingsRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('Raw settings data:', JSON.stringify(data, null, 2));
          
          // Check if data.rates and data.rates.accommodation exist
          if (!data?.rates?.accommodation) {
            console.error('No rates.accommodation found in settings');
            setSettings(null);
            return;
          }
          
          console.log('Raw rates data:', JSON.stringify(data.rates, null, 2));
          console.log('Raw accommodation rates:', JSON.stringify(data.rates.accommodation, null, 2));
          
          // Check if we have cottage rates
          if (!data.rates.accommodation.cottages) {
            console.error('No cottages rates found');
            setSettings(null);
            return;
          }
          
          console.log('Raw cottage rates:', JSON.stringify(data.rates.accommodation.cottages, null, 2));
          
          // Map the rates to match our RatesByType interface
          const mappedRates: RatesByType = {
            cottages: {
              C1: data.rates.accommodation.cottages?.C1 || { weekly: 0, nightly: 0 },
              C2: data.rates.accommodation.cottages?.C2 || { weekly: 0, nightly: 0 },
              C3: data.rates.accommodation.cottages?.C3 || { weekly: 0, nightly: 0 },
              P1: data.rates.accommodation.cottages?.P1 || { weekly: 0, nightly: 0 },
              P2: data.rates.accommodation.cottages?.P2 || { weekly: 0, nightly: 0 },
              P3: data.rates.accommodation.cottages?.P3 || { weekly: 0, nightly: 0 }
            },
            apartments: {
              A1: data.rates.accommodation.apartments?.A1 || { weekly: 0, nightly: 0 },
              A2: data.rates.accommodation.apartments?.A2 || { weekly: 0, nightly: 0 },
              A3: data.rates.accommodation.apartments?.A3 || { weekly: 0, nightly: 0 }
            },
            dorm: {
              D1: data.rates.accommodation.dorm?.D1 || { weekly: 0, nightly: 0 },
              D2: data.rates.accommodation.dorm?.D2 || { weekly: 0, nightly: 0 }
            },
            campsite: {
              T1: data.rates.accommodation.campsite?.T1 || { weekly: 0, nightly: 0 },
              T2: data.rates.accommodation.campsite?.T2 || { weekly: 0, nightly: 0 }
            }
          };
          
          console.log('Mapped rates structure:', JSON.stringify(mappedRates, null, 2));
          console.log('Looking for C3 rate:', JSON.stringify(mappedRates.cottages.C3, null, 2));
          setAnnualRates(mappedRates);
          setSettings(data);
        } else {
          console.error('Settings document does not exist');
          setSettings(null);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        setSettings(null);
      }
    };
    fetchSettings();
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    setError('');
    try {
      // Check if Firebase is initialized
      if (!db) {
        throw new Error('Firebase is not initialized');
      }

      // First fetch the inventory
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      console.log('Fetching inventory from:', inventoryRef.path);
      
      try {
        const docSnap = await getDoc(inventoryRef);
        const data = docSnap.data();
        console.log('Raw Firestore data:', data);

        if (!docSnap.exists() || !data) {
          console.error('No inventory document found or data is null');
          throw new Error('No inventory document found');
        }

        // Initialize inventory state with empty arrays if data fields are undefined
        const newInventory: InventoryState = {
          Cottage: Array.isArray(data.Cottage) ? data.Cottage : [],
          Apartment: Array.isArray(data.Apartment) ? data.Apartment : [],
          dormA: Array.isArray(data.dormA) ? data.dormA : [],
          dormB: Array.isArray(data.dormB) ? data.dormB : [],
          Fireside: Array.isArray(data.Fireside) ? data.Fireside : [],
          LowerCourt: Array.isArray(data.LowerCourt) ? data.LowerCourt : [],
          UpperCourt: Array.isArray(data.UpperCourt) ? data.UpperCourt : [],
          privateCottage: Array.isArray(data.privateCottage) ? data.privateCottage : []
        };

        // Log each category's length
        Object.entries(newInventory).forEach(([key, value]) => {
          console.log(`${key} count:`, value.length);
        });

        // Initialize stay history arrays (always start empty - we build from primaryCampers)
        Object.keys(newInventory).forEach(key => {
          const typedKey = key as keyof InventoryState;
          newInventory[typedKey] = newInventory[typedKey].map(acc => ({
            ...acc,
            stayHistory: []
          }));
        });

        console.log('Initial inventory state:', newInventory);

        try {
          // First get all campers for name lookup
          console.log('==================== LOADING PRIMARY CAMPERS ====================');
          const primaryCampersQuery = query(collection(db, 'primaryCampers'));
          console.log('Fetching primary campers from:', primaryCampersQuery);
          
          const primaryCampersSnapshot = await getDocs(primaryCampersQuery);
          console.log('Got primaryCampers snapshot, size:', primaryCampersSnapshot.size);

          if (primaryCampersSnapshot.empty) {
            console.warn('No primary campers found in the collection');
            setInventory(newInventory);
            setLoading(false);
            return;
          }

          // Process each primary camper document
          for (const camperDoc of primaryCampersSnapshot.docs) {
            const data = camperDoc.data();
            const guestName = `${data.FirstName || ''} ${data.LastName || ''}`.trim() || 'Unknown Guest';
            
            // Read from accommodationHistory array
            const accommodationHistory = data.accommodationHistory;
            if (!accommodationHistory || !Array.isArray(accommodationHistory)) {
              continue;
            }

            // Process each entry in the accommodationHistory array
            accommodationHistory.forEach((entry: { year: number; placeCode: string }) => {
              const placeCode = entry.placeCode;
              const year = entry.year;
              
              // Skip entries with no valid place code
              if (!placeCode || placeCode === 'No History' || placeCode === 'Did Not Attend') {
                return;
              }

              // Find the accommodation in any category
              Object.entries(newInventory).forEach(([category, accommodations]) => {
                const index = accommodations.findIndex((acc: Accommodation) => 
                  acc.placeCode.toLowerCase() === placeCode.toLowerCase()
                );
                
                if (index !== -1) {
                  // Add to stay history
                  newInventory[category as keyof InventoryState][index].stayHistory.push({
                    year,
                    guestName,
                    registrationId: camperDoc.id,
                    startDate: '',
                    endDate: ''
                  });
                }
              });
            });
          }

          // Sort stay history by year for each accommodation
          Object.keys(newInventory).forEach(category => {
            const typedCategory = category as keyof InventoryState;
            newInventory[typedCategory] = newInventory[typedCategory].map(acc => ({
              ...acc,
              stayHistory: acc.stayHistory.sort((a, b) => b.year - a.year)
            }));
          });

          console.log('Final inventory state:', newInventory);
          setInventory(newInventory);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching primary campers:', err);
          setError('Failed to load primary campers');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try refreshing the page.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to load inventory data');
      setLoading(false);
    }
  };

  const fetchAnnualRates = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'camp');
      const docSnap = await getDoc(settingsRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.rates) {
          // Map the rates to match our RatesByType interface
          const mappedRates: RatesByType = {
            cottages: {
              C1: data.rates.accommodation.cottages.C1 || { weekly: 0, nightly: 0 },
              C2: data.rates.accommodation.cottages.C2 || { weekly: 0, nightly: 0 },
              C3: data.rates.accommodation.cottages.C3 || { weekly: 0, nightly: 0 },
              P1: data.rates.accommodation.cottages.P1 || { weekly: 0, nightly: 0 },
              P2: data.rates.accommodation.cottages.P2 || { weekly: 0, nightly: 0 },
              P3: data.rates.accommodation.cottages.P3 || { weekly: 0, nightly: 0 }
            },
            apartments: {
              A1: data.rates.accommodation.apartments?.A1 || { weekly: 0, nightly: 0 },
              A2: data.rates.accommodation.apartments?.A2 || { weekly: 0, nightly: 0 },
              A3: data.rates.accommodation.apartments?.A3 || { weekly: 0, nightly: 0 }
            },
            dorm: {
              D1: data.rates.accommodation.dorm?.D1 || { weekly: 0, nightly: 0 },
              D2: data.rates.accommodation.dorm?.D2 || { weekly: 0, nightly: 0 }
            },
            campsite: {
              T1: data.rates.accommodation.campsite?.T1 || { weekly: 0, nightly: 0 },
              T2: data.rates.accommodation.campsite?.T2 || { weekly: 0, nightly: 0 }
            }
          };
          
          console.log('Mapped annual rates:', JSON.stringify(mappedRates, null, 2));
          setAnnualRates(mappedRates);
          setError('');
        } else {
          setError('Please set up camp rates in the Settings page first.');
        }
      } else {
        setError('Please set up camp rates in the Settings page first.');
      }
    } catch (err) {
      console.error('Error fetching annual rates:', err);
      setError('Failed to load annual rates. Please try refreshing the page.');
    }
  };

  const resetDonateDraft = () => {
    setPendingDonateFrom(null);
    setShowDonatePricePicker(false);
    setEditSnapshot(null);
  };

  const applyDonateConversion = (cottagePriceCode: string) => {
    if (!selectedAccommodation) return;

    const owner = selectedAccommodation.privateCottageInfo;
    setSelectedAccommodation({
      ...selectedAccommodation,
      priceCode: cottagePriceCode,
      accommodationType: 'Donated',
      placeType: 'Cottage',
      donorInfo: {
        donorName: owner?.ownerName || '',
        donorEmail: owner?.ownerEmail || '',
        donorPhone: owner?.ownerPhone || '',
        donorAddress: owner?.ownerAddress || '',
      },
      privateCottageInfo: undefined,
    });
    setPendingDonateFrom('privateCottage');
    setShowDonatePricePicker(false);
    setError('');
  };

  const handleDonate = () => {
    if (!selectedAccommodation) return;

    const normalizedPriceCode = String(selectedAccommodation.priceCode || '').trim().toUpperCase();
    const mappedCottageCode = ({ P1: 'C1', P2: 'C2', P3: 'C3' } as Record<string, string>)[normalizedPriceCode];

    if (mappedCottageCode) {
      applyDonateConversion(mappedCottageCode);
      return;
    }

    setShowDonatePricePicker(true);
    setError(`Price code "${selectedAccommodation.priceCode || 'blank'}" is not P1, P2, or P3. Choose a cottage rate to donate this unit.`);
  };

  const handleSave = async () => {
    if (!selectedAccommodation) return;
    
    setLoading(true);
    try {
      // Start with the current accommodation
      const updatedAccommodation = { ...selectedAccommodation };

      // Ensure arrays exist
      updatedAccommodation.facilities = updatedAccommodation.facilities || [];
      updatedAccommodation.stayHistory = updatedAccommodation.stayHistory || [];

      // Handle privateCottageInfo based on type
      if (updatedAccommodation.accommodationType === 'Private') {
        updatedAccommodation.privateCottageInfo = {
          ownerName: updatedAccommodation.privateCottageInfo?.ownerName || '',
          ownerEmail: updatedAccommodation.privateCottageInfo?.ownerEmail || '',
          ownerPhone: updatedAccommodation.privateCottageInfo?.ownerPhone || '',
          ownerAddress: updatedAccommodation.privateCottageInfo?.ownerAddress || ''
        };
      } else {
        delete updatedAccommodation.privateCottageInfo;
      }

      // Handle funderInfo based on type
      if (updatedAccommodation.accommodationType === 'Funded') {
        updatedAccommodation.funderInfo = {
          funderName: updatedAccommodation.funderInfo?.funderName || '',
          funderEmail: updatedAccommodation.funderInfo?.funderEmail || '',
          funderPhone: updatedAccommodation.funderInfo?.funderPhone || '',
          funderAddress: updatedAccommodation.funderInfo?.funderAddress || ''
        };
      } else {
        delete updatedAccommodation.funderInfo;
      }

      if (updatedAccommodation.accommodationType === 'Donated') {
        updatedAccommodation.donorInfo = {
          donorName: updatedAccommodation.donorInfo?.donorName || '',
          donorEmail: updatedAccommodation.donorInfo?.donorEmail || '',
          donorPhone: updatedAccommodation.donorInfo?.donorPhone || '',
          donorAddress: updatedAccommodation.donorInfo?.donorAddress || ''
        };
      } else {
        delete updatedAccommodation.donorInfo;
      }

      // Ensure other fields have default values
      updatedAccommodation.notes = updatedAccommodation.notes || '';
      updatedAccommodation.location = updatedAccommodation.location || '';
      updatedAccommodation.status = updatedAccommodation.status || 'available';
      updatedAccommodation.capacity = updatedAccommodation.capacity || 0;

      // Remove any undefined values
      (Object.keys(updatedAccommodation) as Array<keyof typeof updatedAccommodation>).forEach(key => {
        if (updatedAccommodation[key] === undefined) {
          delete updatedAccommodation[key];
        }
      });

      console.log('Updating Firestore document with:', updatedAccommodation);
      
      // Update the accommodation in Firestore
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const docSnap = await getDoc(inventoryRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();

        if (pendingDonateFrom === 'privateCottage') {
          updatedAccommodation.placeType = 'Cottage';
          const privateCottage = (data.privateCottage || []).filter(
            (acc: Accommodation) => acc.id !== selectedAccommodation.id
          );
          const cottageArray = data.Cottage || [];
          const existsInCottage = cottageArray.some((acc: Accommodation) => acc.id === selectedAccommodation.id);
          const Cottage = existsInCottage
            ? cottageArray.map((acc: Accommodation) =>
                acc.id === selectedAccommodation.id ? updatedAccommodation : acc
              )
            : [...cottageArray, updatedAccommodation];

          await setDoc(inventoryRef, {
            ...data,
            privateCottage,
            Cottage,
          });
        } else {
          const accommodationType = selectedAccommodation.placeType;
          const existingArray = data[accommodationType] || [];
          const existsInFirestore = existingArray.some((acc: Accommodation) => acc.id === selectedAccommodation.id);
          const updatedAccommodations = existsInFirestore
            ? existingArray.map((acc: Accommodation) =>
                acc.id === selectedAccommodation.id ? updatedAccommodation : acc
              )
            : [...existingArray, updatedAccommodation];

          await setDoc(inventoryRef, {
            ...data,
            [accommodationType]: updatedAccommodations
          });
        }

        console.log('Firestore update successful');
        
        // Refresh the inventory data
        await fetchInventory();
        
        setSuccess('Changes saved successfully');
        setEditMode(false);
        resetDonateDraft();
      }
    } catch (err: any) {
      console.error('Error saving changes:', err);
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addAccommodation = (type: keyof InventoryState) => {
    const newAccommodation: Accommodation = {
      id: Date.now().toString(),
      placeCode: '',
      description: '',
      status: 'available',
      priceCode: type === 'privateCottage' ? 'P1' : '', // Set default price code for private cottages
      facilities: [],
      stayHistory: [],
      accommodationType: type === 'privateCottage' ? 'Private' : 'Standard', // Default to Standard for non-private cottages
      privateCottageInfo: type === 'privateCottage' ? {
        ownerName: '',
        ownerEmail: '',
        ownerPhone: '',
        ownerAddress: ''
      } : undefined,
      notes: '',
      location: '',
      capacity: 0,
      placeType: type,
      placeName: '',
      placeNumber: '',
    };

    setInventory(prev => ({
      ...prev,
      [type]: [...prev[type], newAccommodation]
    }));
    setSelectedAccommodation(newAccommodation);
    setEditMode(true);
  };

  const updateAccommodation = (accommodation: Accommodation) => {
    setInventory(prev => ({
      ...prev,
      [accommodation.placeType]: prev[accommodation.placeType].map((item: Accommodation) => 
        item.id === accommodation.id ? accommodation : item
      )
    }));
  };

  const deleteAccommodation = (accommodation: Accommodation) => {
    setInventory(prev => ({
      ...prev,
      [accommodation.placeType]: prev[accommodation.placeType].filter((item: Accommodation) => 
        item.id !== accommodation.id
      )
    }));
    setSelectedAccommodation(null);
    setEditMode(false);
  };

  const handleDeleteAccommodation = async () => {
    if (!selectedAccommodation) return;

    setLoading(true);
    try {
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const docSnap = await getDoc(inventoryRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const accommodationType = selectedAccommodation.placeType;
        const existingArray = data[accommodationType] || [];
        const updatedAccommodations = existingArray.filter(
          (acc: Accommodation) => acc.id !== selectedAccommodation.id
        );

        await setDoc(inventoryRef, {
          ...data,
          [accommodationType]: updatedAccommodations
        });

        await fetchInventory();

        setSuccess(`Unit "${selectedAccommodation.placeCode}" deleted successfully`);
        setTimeout(() => setSuccess(''), 3000);
        setShowDeleteConfirm(false);
        setDeleteConfirmText('');
        setSelectedAccommodation(null);
        setEditMode(false);
      }
    } catch (err: any) {
      console.error('Error deleting accommodation:', err);
      setError(`Failed to delete unit: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getAvailablePriceCodes = (type: keyof InventoryState): PriceCodeType[] => {
    const rateType = getAccommodationType(type);
    return PRICE_CODES.filter(code => code.type === rateType);
  };

  const clearFilters = () => {
    setFilterType('all');
    setSelectedType(null);
    setSelectedPriceCode(null);
    setSelectedAccommodationType(null);
  };

  const getFilteredInventory = () => {
    console.log('Current inventory state:', inventory);
    console.log('Current filter type:', filterType);
    console.log('Selected type:', selectedType);
    console.log('Selected price code:', selectedPriceCode);
    console.log('Selected accommodation type:', selectedAccommodationType);
    
    // Start with a copy of the current inventory
    let filtered: InventoryState = JSON.parse(JSON.stringify(inventory));
    
    switch (filterType) {
      case 'all':
        // Return all inventory without filtering
        break;
      case 'byType':
        if (selectedType) {
          // Special handling for Funded - it's an accommodation type, not a place type
          if (selectedType === 'Funded') {
            Object.keys(filtered).forEach(key => {
              filtered[key as keyof InventoryState] = filtered[key as keyof InventoryState]
                .filter(acc => acc.accommodationType === 'Funded');
            });
          } else if (selectedType === 'Maintenance') {
            Object.keys(filtered).forEach(key => {
              filtered[key as keyof InventoryState] = filtered[key as keyof InventoryState]
                .filter(acc => String(acc.status || '').toLowerCase() === 'maintenance');
            });
          } else {
            // Only filter if a type is selected
            Object.keys(filtered).forEach(key => {
              if (key !== selectedType) {
                filtered[key as keyof InventoryState] = [];
              }
            });
          }
        }
        break;
      case 'unassignedByType':
        if (selectedType) {
          // Special handling for Funded - it's an accommodation type, not a place type
          if (selectedType === 'Funded') {
            Object.keys(filtered).forEach(key => {
              filtered[key as keyof InventoryState] = filtered[key as keyof InventoryState]
                .filter(acc => acc.status === 'available' && acc.accommodationType === 'Funded');
            });
          } else {
            // Only filter if a type is selected
            Object.keys(filtered).forEach(key => {
              if (key === selectedType) {
                filtered[key as keyof InventoryState] = filtered[key as keyof InventoryState]
                  .filter(acc => acc.status === 'available');
              } else {
                filtered[key as keyof InventoryState] = [];
              }
            });
          }
        }
        break;
      case 'unassignedByPriceCode':
        if (selectedPriceCode) {
          // Only filter if a price code is selected
          Object.keys(filtered).forEach(key => {
            filtered[key as keyof InventoryState] = filtered[key as keyof InventoryState]
              .filter(acc => acc.status === 'available' && acc.priceCode === selectedPriceCode);
          });
        }
        break;
      case 'byAccommodationType':
        if (selectedAccommodationType) {
          // Filter all categories by accommodation type
          Object.keys(filtered).forEach(key => {
            filtered[key as keyof InventoryState] = filtered[key as keyof InventoryState]
              .filter(acc => acc.accommodationType === selectedAccommodationType);
          });
        }
        break;
    }

    // Sort each category by number
    Object.keys(filtered).forEach(key => {
      filtered[key as keyof InventoryState].sort((a, b) => {
        const numA = parseInt(a.placeNumber) || 0;
        const numB = parseInt(b.placeNumber) || 0;
        return numA - numB;
      });
    });

    console.log('Filtered inventory:', filtered);
    return filtered;
  };

  const getInventoryFilterLabel = (): string => {
    switch (filterType) {
      case 'byType':
        return selectedType
          ? `By Type: ${TYPE_FILTER_LABELS[selectedType]}`
          : 'By Type';
      case 'unassignedByType':
        return selectedType
          ? `Unassigned by Type: ${TYPE_FILTER_LABELS[selectedType]}`
          : 'Unassigned by Type';
      case 'unassignedByPriceCode':
        return selectedPriceCode
          ? `Unassigned by Price Code: ${selectedPriceCode}`
          : 'Unassigned by Price Code';
      case 'byAccommodationType':
        return 'Staff Housing';
      default:
        return 'All Inventory';
    }
  };

  const countFilteredUnits = (filtered: InventoryState): number => {
    return (Object.keys(filtered) as (keyof InventoryState)[]).reduce(
      (sum, key) => sum + filtered[key].length,
      0
    );
  };

  const handlePrint = () => {
    window.print();
  };

  // Helper function to check if a section has any items
  const sectionHasItems = (type: keyof InventoryState) => {
    return getFilteredInventory()[type].length > 0;
  };

  // Add this function to find accommodation by PlaceCode
  const findAccommodationByPlaceCode = (placeCode: string) => {
    console.log('Searching for place code:', placeCode);
    console.log('Current inventory state:', inventory);
    
    // Categories to search through
    const categories: (keyof InventoryState)[] = [
      'Cottage',
      'Apartment',
      'dormA',
      'dormB',
      'Fireside',
      'LowerCourt',
      'UpperCourt',
      'privateCottage'
    ];
    
    for (const category of categories) {
      console.log('Searching in category:', category);
      const found = inventory[category].find(room => {
        console.log('Comparing:', room.placeCode, 'with:', placeCode);
        return room.placeCode.toLowerCase() === placeCode.toLowerCase();
      });
      
      if (found) {
        console.log('Found accommodation:', found);
        setSelectedAccommodation(found);
        setError('');
        return;
      }
    }
    
    console.log('No accommodation found');
    setError(`No accommodation found with Place Code ${placeCode}`);
    setSelectedAccommodation(null);
  };

  // Add handler for PlaceCode search
  const handlePlaceCodeSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (placeCodeSearch.trim()) {
      findAccommodationByPlaceCode(placeCodeSearch.trim());
    }
  };

  // Helper function to get the full camp rate
  const getFullCampRate = (priceCode: string): number => {
    console.log('Getting full camp rate for price code:', priceCode);
    console.log('Current annualRates:', annualRates);
    
    if (!annualRates) {
      console.log('No annual rates available');
      return 0;
    }
    
    // Convert priceCode to uppercase for case-insensitive comparison
    const upperPriceCode = priceCode.toUpperCase();
    console.log('Looking up rate for:', upperPriceCode);
    
    // Use PRICE_CODES directly since it's already of type PriceCodeType[] which extends PriceCode[]
    const rate = getRateForPriceCode(upperPriceCode, annualRates, PRICE_CODES);
    
    console.log('Rate found:', rate);
    return rate?.weekly || 0; // Rate from settings is already for full camp
  };

  const formatWeeklyPrice = (priceCode?: string): string => {
    if (!priceCode?.trim() || !annualRates) return '';
    const rate = getRateForPriceCode(priceCode.trim().toUpperCase(), annualRates, PRICE_CODES);
    if (!rate || typeof rate.weekly !== 'number') return '';
    return rate.weekly.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const handleFieldChange = (field: keyof Accommodation, value: string | PrivateCottageInfo | FunderInfo | DonorInfo) => {
    if (!selectedAccommodation) return;
    
    setSelectedAccommodation({
      ...selectedAccommodation,
      [field]: value
    });
  };

  const handleFacilityChange = (facility: string, checked: boolean) => {
    if (!selectedAccommodation) return;
    
    setSelectedAccommodation({
      ...selectedAccommodation,
      facilities: checked 
        ? [...selectedAccommodation.facilities, facility]
        : selectedAccommodation.facilities.filter(f => f !== facility)
    });
  };

  const handleSaveFacilitiesData = async (facilitiesData: FacilitiesData) => {
    if (!selectedAccommodation) return;

    try {
      // Update the selected accommodation with facilities data
      const updatedAccommodation = {
        ...selectedAccommodation,
        facilitiesData
      };

      setSelectedAccommodation(updatedAccommodation);

      // Save to Firestore
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const docSnap = await getDoc(inventoryRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const accommodationType = selectedAccommodation.placeType;

        const updatedAccommodations = data[accommodationType].map((acc: Accommodation) =>
          acc.id === selectedAccommodation.id
            ? { ...acc, facilitiesData }
            : acc
        );

        await setDoc(inventoryRef, {
          ...data,
          [accommodationType]: updatedAccommodations
        });

        // Update local inventory state
        setInventory(prev => ({
          ...prev,
          [accommodationType]: prev[accommodationType].map((acc: Accommodation) =>
            acc.id === selectedAccommodation.id
              ? { ...acc, facilitiesData }
              : acc
          )
        }));

        setSuccess('Facilities details saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      console.error('Error saving facilities data:', err);
      setError(`Failed to save facilities: ${err.message}`);
    }
  };

  const renderDashboard = (accommodation: Accommodation) => {
    const weeklyRate = getFullCampRate(accommodation.priceCode);
    
    const cellStyle = {
      display: 'flex',
      alignItems: 'center',
      padding: '1.5rem',
      backgroundColor: 'white',
      border: '1px solid #eee',
      borderRadius: '8px',
      minWidth: '250px',
      gap: '12px'
    };
    
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '1.5rem',
        marginBottom: '2rem',
        width: '100%'
      }}>
        <div style={cellStyle}>
          <strong style={{ color: '#666', whiteSpace: 'nowrap' }}>Status:</strong>
          <span style={{ 
            color: accommodation.status === 'available' ? '#2e7d32' : 
                   accommodation.status === 'occupied' ? '#c62828' : '#ed6c02',
            fontWeight: 500
          }}>
            {accommodation.status.charAt(0).toUpperCase() + accommodation.status.slice(1)}
          </span>
        </div>

        <div style={cellStyle}>
          <strong style={{ color: '#666', whiteSpace: 'nowrap' }}>Weekly Rate:</strong>
          <span style={{ fontWeight: 500 }}>${weeklyRate.toFixed(2)}</span>
        </div>

        <div 
          style={{
            ...cellStyle,
            flex: 1,
            minWidth: 'auto',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onClick={() => setShowFacilitiesModal(true)}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
          title="Click to view/edit facilities details"
        >
          <strong style={{ color: '#666', whiteSpace: 'nowrap' }}>Facilities:</strong>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
            {accommodation.facilities.map((facility, index) => (
              <span key={index} style={{
                backgroundColor: '#f5f5f5',
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '0.85rem',
                color: '#666',
                border: '1px solid #eee'
              }}>
                {facility}
              </span>
            ))}
            <span style={{
              backgroundColor: '#e7f3ff',
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '0.85rem',
              color: '#0a58ca',
              border: '1px solid #b6d4fe'
            }}>
              + Details
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPhotoPanel = () => {
    return (
      <div className="photo-panel">
        <h3>Photo</h3>
        <div className="coming-soon">
          Coming Soon
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <style>{`
          .inventory-management {
            max-width: 1200px;
            margin: 0 auto;
            padding: 2rem;
          }

          .error-message {
            background: #fee;
            color: #e74c3c;
            padding: 1rem;
            border-radius: 4px;
            margin-bottom: 1rem;
          }

          .error-link {
            color: #e74c3c;
            text-decoration: underline;
            font-weight: 600;
            margin-left: 0.5rem;
          }

          .error-link:hover {
            color: #c0392b;
          }

          .success-message {
            background: #efe;
            color: #27ae60;
            padding: 1rem;
            border-radius: 4px;
            margin-bottom: 1rem;
          }

          .inventory-section {
            background: white;
            border-radius: 8px;
            padding: 2rem;
            margin-bottom: 2rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .no-items {
            color: #666;
            text-align: center;
            padding: 2rem;
            background: #f8f9fa;
            border-radius: 4px;
            margin: 1rem 0;
          }

          .inventory-section h2 {
            margin-top: 0;
            margin-bottom: 1rem;
            color: #2c3e50;
          }

          .inventory-section button {
            margin-bottom: 1rem;
          }

          .clickable-row {
            cursor: pointer;
          }

          .clickable-row:hover {
            background-color: #f5f5f5;
          }

          .place-code-search {
            margin-bottom: 1rem;
            display: flex;
            justify-content: center;
          }

          .place-code-search form {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }

          .place-code-input {
            padding: 0.5rem;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 1rem;
            width: 150px;
          }

          .search-button {
            padding: 0.5rem 1rem;
            background-color: #2c3e50;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          }

          .search-button:hover {
            background-color: #34495e;
          }
        `}</style>
        <div>
          <div className="inventory-management">
            <h1>Inventory Management</h1>
            <div className="loading">Loading inventory data...</div>
          </div>
        </div>
      </>
    );
  }

  const filteredInventory = getFilteredInventory();
  const filteredUnitCount = countFilteredUnits(filteredInventory);
  const printDate = new Date().toLocaleDateString();

  return (
    <div className="admin-content inventory-management-page">
      <h1>Inventory Management</h1>
      {error && (
        <div style={{
          background: '#fee',
          color: '#c62828',
          padding: '0.75rem 1rem',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          background: '#efe',
          color: '#2e7d32',
          padding: '0.75rem 1rem',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {success}
        </div>
      )}
      <div className="inventory-print-header">
        <h1>Inventory</h1>
        <p>
          {getInventoryFilterLabel()} — {filteredUnitCount} unit{filteredUnitCount === 1 ? '' : 's'} — Printed {printDate}
        </p>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="place-code-search">
          <form onSubmit={handlePlaceCodeSearch}>
            <input
              type="text"
              value={placeCodeSearch}
              onChange={(e) => setPlaceCodeSearch(e.target.value)}
              placeholder="Enter Place Code"
              className="place-code-input"
            />
            <button type="submit" className="search-button">Find</button>
          </form>
        </div>

        <div className="filter-buttons">
          <button 
            className={filterType === 'all' ? 'active' : ''} 
            onClick={() => clearFilters()}
          >
            All Inventory
          </button>
          
          <button 
            className={filterType === 'byType' ? 'active' : ''} 
            onClick={() => setFilterType('byType')}
          >
            By Type
          </button>
          
          <button 
            className={filterType === 'unassignedByType' ? 'active' : ''} 
            onClick={() => setFilterType('unassignedByType')}
          >
            Unassigned by Type
          </button>

          <button 
            className={filterType === 'unassignedByPriceCode' ? 'active' : ''} 
            onClick={() => setFilterType('unassignedByPriceCode')}
          >
            Unassigned by Price Code
          </button>

          <button 
            className={filterType === 'byAccommodationType' ? 'active' : ''}
            onClick={() => {
              setFilterType('byAccommodationType');
              setSelectedAccommodationType('Staff');
            }}
          >
            Staff Housing
          </button>
        </div>

        {/* Hidden select for Staff filter */}
        {filterType === 'byAccommodationType' && (
          <div className="filter-select" style={{ display: 'none' }}>
            <select
              value="Staff"
              onChange={() => {}}
              className="filter-dropdown"
            >
              <option value="Staff">Staff</option>
            </select>
          </div>
        )}

        {/* Type Selection Dropdown - Show when filterType is 'byType' or 'unassignedByType' */}
        {(filterType === 'byType' || filterType === 'unassignedByType') && (
          <div className="filter-select">
            <select
              value={selectedType || ''}
              onChange={(e) => setSelectedType(e.target.value as TypeFilterSelection)}
              className="filter-dropdown"
            >
              <option value="">Select Type</option>
              <option value="Cottage">Cottage</option>
              <option value="Apartment">Apartment</option>
              <option value="dormA">Dorm A</option>
              <option value="dormB">Dorm B</option>
              <option value="Fireside">Fireside</option>
              <option value="LowerCourt">Lower Court</option>
              <option value="UpperCourt">Upper Court</option>
              <option value="privateCottage">Private Cottage</option>
              <option value="Funded">Funded</option>
              {filterType === 'byType' && (
                <option value="Maintenance">Maintenance</option>
              )}
            </select>
          </div>
        )}

        {/* Price Code Selection Dropdown - Show when filterType is 'unassignedByPriceCode' */}
        {filterType === 'unassignedByPriceCode' && (
          <div className="filter-select">
            <select
              value={selectedPriceCode || ''}
              onChange={(e) => setSelectedPriceCode(e.target.value)}
              className="filter-dropdown"
            >
              <option value="">Select Price Code</option>
              {PRICE_CODES.map((code) => (
                <option key={code.code} value={code.code}>
                  {code.description}
                </option>
              ))}
            </select>
          </div>
        )}

        {!selectedAccommodation && (
          <div className="inventory-print-actions">
            <button type="button" onClick={handlePrint} className="inventory-print-button">
              Print View
            </button>
          </div>
        )}
      </div>

      {selectedAccommodation ? (
        <div className="selected-accommodation">
          <div className="modal-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button
                onClick={() => {
                  setSelectedAccommodation(null);
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                  setEditMode(false);
                  resetDonateDraft();
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#666'
                }}
              >
                ← Back
              </button>
              <h2>
                {editMode ? 'Edit' : 'View'} Accommodation - {selectedAccommodation.placeName} "{selectedAccommodation.placeCode}"
              </h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {editMode && selectedAccommodation.placeType === 'privateCottage' && (
                <button
                  onClick={handleDonate}
                  style={{
                    padding: '0.5rem 1.5rem',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  Donate
                </button>
              )}
              <button 
                onClick={() => {
                  if (editMode) {
                    if (editSnapshot) {
                      setSelectedAccommodation(editSnapshot);
                    }
                    resetDonateDraft();
                    setError('');
                    setEditMode(false);
                  } else {
                    setEditSnapshot(selectedAccommodation);
                    setPendingDonateFrom(null);
                    setShowDonatePricePicker(false);
                    setEditMode(true);
                  }
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }} 
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: editMode ? '#ef5350' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              >
                {editMode ? 'Cancel Edit' : 'Edit'}
              </button>
            </div>
          </div>

          {showDonatePricePicker && (
            <div style={{
              background: '#fff8e1',
              border: '1px solid #ffe082',
              borderRadius: '8px',
              padding: '1rem 1.25rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: '0 0 0.75rem 0', color: '#666' }}>
                Choose the cottage rate for this donated unit.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {PRICE_CODES.filter((code) => code.code === 'C1' || code.code === 'C2' || code.code === 'C3').map((code) => (
                  <button
                    key={code.code}
                    type="button"
                    onClick={() => applyDonateConversion(code.code)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: 'white',
                      border: '1px solid #1976d2',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#1976d2',
                      fontWeight: 500
                    }}
                  >
                    {code.code} — {code.description}
                  </button>
                ))}
              </div>
            </div>
          )}

          {renderDashboard(selectedAccommodation)}
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            {/* Left side - Photo panel */}
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #eee',
              height: 'fit-content'
            }}>
              <h3 style={{ 
                margin: '0 0 1rem 0',
                fontSize: '1.1rem',
                color: '#666'
              }}>Photo</h3>
              <div style={{
                borderRadius: '8px',
                overflow: 'hidden',
                textAlign: 'center'
              }}>
                <img 
                  src={`https://firebasestorage.googleapis.com/v0/b/berwickregistration.firebasestorage.app/o/Images%2F${encodeURIComponent(selectedAccommodation.placeCode.toUpperCase())}%2F${encodeURIComponent(selectedAccommodation.placeCode.toUpperCase())}.jpg?alt=media`}
                  alt={selectedAccommodation.placeCode}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: '8px'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    if (target.parentElement) {
                      target.parentElement.innerHTML = '<div style="padding: 6rem 1.5rem; border: 2px dashed #eee; border-radius: 8px; color: #666;">No Photo Available</div>';
                    }
                  }}
                />
              </div>
            </div>

            {/* Middle - Info */}
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #eee'
            }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem' 
                }}>
                  <label style={{ 
                    color: '#666',
                    minWidth: '140px',
                    fontWeight: 500
                  }}>Place Code -</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={selectedAccommodation.placeCode}
                      onChange={(e) => handleFieldChange('placeCode', e.target.value)}
                    />
                  ) : (
                    <span>{selectedAccommodation.placeCode}</span>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem' 
                }}>
                  <label style={{ 
                    color: '#666',
                    minWidth: '140px',
                    fontWeight: 500
                  }}>Place Name -</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={selectedAccommodation.placeName}
                      onChange={(e) => handleFieldChange('placeName', e.target.value)}
                    />
                  ) : (
                    <span>{selectedAccommodation.placeName}</span>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem' 
                }}>
                  <label style={{ 
                    color: '#666',
                    minWidth: '140px',
                    fontWeight: 500
                  }}>Number -</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={selectedAccommodation.placeNumber}
                      onChange={(e) => handleFieldChange('placeNumber', e.target.value)}
                    />
                  ) : (
                    <span>{selectedAccommodation.placeNumber}</span>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem' 
                }}>
                  <label style={{ 
                    color: '#666',
                    minWidth: '140px',
                    fontWeight: 500
                  }}>Status -</label>
                  {editMode ? (
                    <select 
                      value={selectedAccommodation.status}
                      onChange={(e) => handleFieldChange('status', e.target.value as Accommodation['status'])}
                      style={{
                        backgroundColor: selectedAccommodation.status === 'available' ? '#e8f5e9' : 
                                       selectedAccommodation.status === 'occupied' ? '#ffebee' : '#fff3e0',
                        color: selectedAccommodation.status === 'available' ? '#2e7d32' : 
                              selectedAccommodation.status === 'occupied' ? '#c62828' : '#ed6c02',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        border: '1px solid',
                        borderColor: selectedAccommodation.status === 'available' ? '#a5d6a7' : 
                                   selectedAccommodation.status === 'occupied' ? '#ef9a9a' : '#ffcc80',
                        fontWeight: 500
                      }}
                    >
                      <option value="available">Available</option>
                      <option value="occupied">Occupied</option>
                      <option value="maintenance">Maintenance</option>
                    </select>
                  ) : (
                    <span style={{ 
                      backgroundColor: selectedAccommodation.status === 'available' ? '#e8f5e9' : 
                                    selectedAccommodation.status === 'occupied' ? '#ffebee' : '#fff3e0',
                      color: selectedAccommodation.status === 'available' ? '#2e7d32' : 
                             selectedAccommodation.status === 'occupied' ? '#c62828' : '#ed6c02',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: selectedAccommodation.status === 'available' ? '#a5d6a7' : 
                                 selectedAccommodation.status === 'occupied' ? '#ef9a9a' : '#ffcc80',
                      fontWeight: 500,
                      textTransform: 'capitalize'
                    }}>
                      {selectedAccommodation.status}
                    </span>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem' 
                }}>
                  <label style={{ 
                    color: '#666',
                    minWidth: '140px',
                    fontWeight: 500
                  }}>Price Code -</label>
                  {editMode ? (
                    <select 
                      value={selectedAccommodation.priceCode}
                      onChange={(e) => handleFieldChange('priceCode', e.target.value)}
                    >
                      <option value="">Select Price Code</option>
                      {getAvailablePriceCodes(selectedAccommodation.placeType).map(code => (
                        <option key={code.code} value={code.code}>
                          {code.description}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{selectedAccommodation.priceCode}</span>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem' 
                }}>
                  <label style={{ 
                    color: '#666',
                    minWidth: '140px',
                    fontWeight: 500
                  }}>Accommodation Type -</label>
                  {editMode ? (
                    <select
                      value={selectedAccommodation.accommodationType || 'Standard'}
                      onChange={(e) => handleFieldChange('accommodationType', e.target.value)}
                    >
                      <option value="Standard">UCCMA</option>
                      <option value="Staff">Staff</option>
                      <option value="Private">Private</option>
                      <option value="Funded">Funded</option>
                      <option value="Donated">Donated</option>
                    </select>
                  ) : (
                    <span>
                      {!selectedAccommodation.accommodationType || selectedAccommodation.accommodationType === 'Standard' ? 'UCCMA' : 
                       selectedAccommodation.accommodationType === 'Private' ? 'Private' : 
                       selectedAccommodation.accommodationType === 'Staff' ? 'Staff' : 
                       selectedAccommodation.accommodationType === 'Funded' ? 'Funded' :
                       selectedAccommodation.accommodationType === 'Donated' ? 'Donated' :
                       'UCCMA'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right side - Stay History */}
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #eee'
            }}>
              <h3 style={{ 
                margin: '0 0 1rem 0',
                fontSize: '1.1rem',
                color: '#666'
              }}>Stay History</h3>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {(() => {
                  // Get current year
                  const currentYear = new Date().getFullYear();
                  // Create array of last 8 years
                  const years = Array.from({ length: 8 }, (_, i) => currentYear - i);
                  
                  // Create a map of existing stays
                  const staysByYear = selectedAccommodation.stayHistory.reduce((acc: { 
                    [key: number]: { guestName: string, registrationId: string }[] 
                  }, stay) => {
                    const year = stay.year || 0;
                    if (!acc[year]) {
                      acc[year] = [];
                    }
                    acc[year].push({
                      guestName: stay.guestName || 'Unknown Guest',
                      registrationId: stay.registrationId || 'unknown'
                    });
                    return acc;
                  }, {});

                  // Map through all years
                  return years.map(year => (
                    <div key={year} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem',
                      borderBottom: '1px solid #eee'
                    }}>
                      <span style={{ fontWeight: 500, minWidth: '60px' }}>{year}</span>
                      <div style={{ 
                        flex: 1,
                        marginLeft: '1rem',
                        color: staysByYear[year] ? 'inherit' : '#999'
                      }}>
                        {staysByYear[year] ? (
                          staysByYear[year].map((guest, index) => (
                            <span key={`${year}-${guest.registrationId}`}>
                              {index > 0 ? ' / ' : ''}
                              <a 
                                href={`/admin/campers/${guest.registrationId}`}
                                style={{ 
                                  color: '#1976d2',
                                  textDecoration: 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                {guest.guestName}
                              </a>
                            </span>
                          ))
                        ) : (
                          'No stay recorded'
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Owner Information - Only show for Private Cottages */}
          {selectedAccommodation?.placeType === 'privateCottage' && (
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #eee',
              marginTop: '1.5rem'
            }}>
              <h3 style={{ 
                margin: '0 0 1rem 0',
                fontSize: '1.1rem',
                color: '#666'
              }}>Owner Information</h3>
              <div style={{ display: 'grid', gap: '0.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Owner Name -</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={selectedAccommodation.privateCottageInfo?.ownerName || ''}
                      onChange={(e) => handleFieldChange('privateCottageInfo', {
                        ownerName: e.target.value,
                        ownerEmail: selectedAccommodation.privateCottageInfo?.ownerEmail || '',
                        ownerPhone: selectedAccommodation.privateCottageInfo?.ownerPhone || '',
                        ownerAddress: selectedAccommodation.privateCottageInfo?.ownerAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.privateCottageInfo?.ownerName || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Email -</label>
                  {editMode ? (
                    <input
                      type="email"
                      value={selectedAccommodation.privateCottageInfo?.ownerEmail || ''}
                      onChange={(e) => handleFieldChange('privateCottageInfo', {
                        ownerName: selectedAccommodation.privateCottageInfo?.ownerName || '',
                        ownerEmail: e.target.value,
                        ownerPhone: selectedAccommodation.privateCottageInfo?.ownerPhone || '',
                        ownerAddress: selectedAccommodation.privateCottageInfo?.ownerAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.privateCottageInfo?.ownerEmail || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Phone -</label>
                  {editMode ? (
                    <input
                      type="tel"
                      value={selectedAccommodation.privateCottageInfo?.ownerPhone || ''}
                      onChange={(e) => handleFieldChange('privateCottageInfo', {
                        ownerName: selectedAccommodation.privateCottageInfo?.ownerName || '',
                        ownerEmail: selectedAccommodation.privateCottageInfo?.ownerEmail || '',
                        ownerPhone: e.target.value,
                        ownerAddress: selectedAccommodation.privateCottageInfo?.ownerAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.privateCottageInfo?.ownerPhone || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500, marginTop: '0.5rem' }}>Address -</label>
                  {editMode ? (
                    <textarea
                      value={selectedAccommodation.privateCottageInfo?.ownerAddress || ''}
                      onChange={(e) => handleFieldChange('privateCottageInfo', {
                        ownerName: selectedAccommodation.privateCottageInfo?.ownerName || '',
                        ownerEmail: selectedAccommodation.privateCottageInfo?.ownerEmail || '',
                        ownerPhone: selectedAccommodation.privateCottageInfo?.ownerPhone || '',
                        ownerAddress: e.target.value
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%',
                        minHeight: '80px',
                        resize: 'vertical'
                      }}
                    />
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedAccommodation.privateCottageInfo?.ownerAddress || 'Not specified'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Funder Information - Only show for Funded accommodations */}
          {selectedAccommodation?.accommodationType === 'Funded' && (
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #eee',
              marginTop: '1.5rem'
            }}>
              <h3 style={{ 
                margin: '0 0 1rem 0',
                fontSize: '1.1rem',
                color: '#666'
              }}>Funder Information</h3>
              <div style={{ display: 'grid', gap: '0.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Funder Name -</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={selectedAccommodation.funderInfo?.funderName || ''}
                      onChange={(e) => handleFieldChange('funderInfo', {
                        funderName: e.target.value,
                        funderEmail: selectedAccommodation.funderInfo?.funderEmail || '',
                        funderPhone: selectedAccommodation.funderInfo?.funderPhone || '',
                        funderAddress: selectedAccommodation.funderInfo?.funderAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.funderInfo?.funderName || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Email -</label>
                  {editMode ? (
                    <input
                      type="email"
                      value={selectedAccommodation.funderInfo?.funderEmail || ''}
                      onChange={(e) => handleFieldChange('funderInfo', {
                        funderName: selectedAccommodation.funderInfo?.funderName || '',
                        funderEmail: e.target.value,
                        funderPhone: selectedAccommodation.funderInfo?.funderPhone || '',
                        funderAddress: selectedAccommodation.funderInfo?.funderAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.funderInfo?.funderEmail || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Phone -</label>
                  {editMode ? (
                    <input
                      type="tel"
                      value={selectedAccommodation.funderInfo?.funderPhone || ''}
                      onChange={(e) => handleFieldChange('funderInfo', {
                        funderName: selectedAccommodation.funderInfo?.funderName || '',
                        funderEmail: selectedAccommodation.funderInfo?.funderEmail || '',
                        funderPhone: e.target.value,
                        funderAddress: selectedAccommodation.funderInfo?.funderAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.funderInfo?.funderPhone || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500, marginTop: '0.5rem' }}>Address -</label>
                  {editMode ? (
                    <textarea
                      value={selectedAccommodation.funderInfo?.funderAddress || ''}
                      onChange={(e) => handleFieldChange('funderInfo', {
                        funderName: selectedAccommodation.funderInfo?.funderName || '',
                        funderEmail: selectedAccommodation.funderInfo?.funderEmail || '',
                        funderPhone: selectedAccommodation.funderInfo?.funderPhone || '',
                        funderAddress: e.target.value
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%',
                        minHeight: '80px',
                        resize: 'vertical'
                      }}
                    />
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedAccommodation.funderInfo?.funderAddress || 'Not specified'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Donor Information - Only show for Donated accommodations */}
          {selectedAccommodation?.accommodationType === 'Donated' && (
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #eee',
              marginTop: '1.5rem'
            }}>
              <h3 style={{ 
                margin: '0 0 1rem 0',
                fontSize: '1.1rem',
                color: '#666'
              }}>Donor Information</h3>
              <div style={{ display: 'grid', gap: '0.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Donor Name -</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={selectedAccommodation.donorInfo?.donorName || ''}
                      onChange={(e) => handleFieldChange('donorInfo', {
                        donorName: e.target.value,
                        donorEmail: selectedAccommodation.donorInfo?.donorEmail || '',
                        donorPhone: selectedAccommodation.donorInfo?.donorPhone || '',
                        donorAddress: selectedAccommodation.donorInfo?.donorAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.donorInfo?.donorName || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Email -</label>
                  {editMode ? (
                    <input
                      type="email"
                      value={selectedAccommodation.donorInfo?.donorEmail || ''}
                      onChange={(e) => handleFieldChange('donorInfo', {
                        donorName: selectedAccommodation.donorInfo?.donorName || '',
                        donorEmail: e.target.value,
                        donorPhone: selectedAccommodation.donorInfo?.donorPhone || '',
                        donorAddress: selectedAccommodation.donorInfo?.donorAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.donorInfo?.donorEmail || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500 }}>Phone -</label>
                  {editMode ? (
                    <input
                      type="tel"
                      value={selectedAccommodation.donorInfo?.donorPhone || ''}
                      onChange={(e) => handleFieldChange('donorInfo', {
                        donorName: selectedAccommodation.donorInfo?.donorName || '',
                        donorEmail: selectedAccommodation.donorInfo?.donorEmail || '',
                        donorPhone: e.target.value,
                        donorAddress: selectedAccommodation.donorInfo?.donorAddress || ''
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <span>{selectedAccommodation.donorInfo?.donorPhone || 'Not specified'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                  <label style={{ color: '#666', minWidth: '140px', fontWeight: 500, marginTop: '0.5rem' }}>Address -</label>
                  {editMode ? (
                    <textarea
                      value={selectedAccommodation.donorInfo?.donorAddress || ''}
                      onChange={(e) => handleFieldChange('donorInfo', {
                        donorName: selectedAccommodation.donorInfo?.donorName || '',
                        donorEmail: selectedAccommodation.donorInfo?.donorEmail || '',
                        donorPhone: selectedAccommodation.donorInfo?.donorPhone || '',
                        donorAddress: e.target.value
                      })}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%',
                        minHeight: '80px',
                        resize: 'vertical'
                      }}
                    />
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedAccommodation.donorInfo?.donorAddress || 'Not specified'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bottom - Notes */}
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid #eee'
          }}>
            <h3 style={{ 
              margin: '0 0 1rem 0',
              fontSize: '1.1rem',
              color: '#666'
            }}>Notes</h3>
            {editMode ? (
              <textarea
                value={selectedAccommodation.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #eee',
                  fontSize: '0.9rem'
                }}
              />
            ) : (
              <p style={{ 
                color: '#666',
                whiteSpace: 'pre-wrap'
              }}>{selectedAccommodation.notes || 'No notes available.'}</p>
            )}
          </div>

          {editMode && (
            <div className="edit-actions">
              <button onClick={handleSave} className="save-button">
                Save Changes
              </button>
              <button 
                onClick={() => setEditMode(false)} 
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Danger Zone - safely separated at the very bottom */}
          {editMode && (
            <div style={{
              marginTop: '6rem',
              paddingTop: '2rem',
              borderTop: '1px dashed #e0e0e0'
            }}>
              <div style={{
                background: '#fffafa',
                border: '1px solid #f3d2d2',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h3 style={{
                  margin: '0 0 0.5rem 0',
                  fontSize: '1rem',
                  color: '#b71c1c'
                }}>Danger Zone</h3>
                <p style={{ margin: '0 0 1rem 0', color: '#666', fontSize: '0.9rem' }}>
                  Permanently delete this unit from inventory. This cannot be undone.
                </p>

                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{
                      padding: '0.5rem 1.25rem',
                      backgroundColor: 'white',
                      color: '#c62828',
                      border: '1px solid #e0a0a0',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    Delete this unit
                  </button>
                ) : (
                  <div style={{
                    background: 'white',
                    border: '1px solid #f3d2d2',
                    borderRadius: '6px',
                    padding: '1rem'
                  }}>
                    <p style={{ margin: '0 0 0.75rem 0', color: '#b71c1c', fontSize: '0.9rem' }}>
                      Please confirm. Type the Place Code <strong>{selectedAccommodation.placeCode}</strong> below to enable deletion.
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Enter Place Code to confirm"
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        width: '220px',
                        marginBottom: '1rem'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        onClick={handleDeleteAccommodation}
                        disabled={
                          deleteConfirmText.trim().toLowerCase() !==
                          selectedAccommodation.placeCode.trim().toLowerCase()
                        }
                        style={{
                          padding: '0.5rem 1.25rem',
                          backgroundColor:
                            deleteConfirmText.trim().toLowerCase() ===
                            selectedAccommodation.placeCode.trim().toLowerCase()
                              ? '#c62828'
                              : '#e0a0a0',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor:
                            deleteConfirmText.trim().toLowerCase() ===
                            selectedAccommodation.placeCode.trim().toLowerCase()
                              ? 'pointer'
                              : 'not-allowed',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        Permanently Delete
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                        }}
                        style={{
                          padding: '0.5rem 1.25rem',
                          backgroundColor: '#f5f5f5',
                          color: '#666',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="inventory-sections">
          {/* Cottage Section */}
          {filteredInventory.Cottage.length > 0 && (
          <div className="inventory-section">
            <h2>Cottage</h2>
            <button onClick={() => addAccommodation('Cottage')}>Add Cottage</button>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Place Code</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Price Code</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.Cottage.map(room => (
                    <tr 
                      key={room.id}
                      onClick={() => setSelectedAccommodation(room)}
                      className="clickable-row"
                    >
                      <td>{room.placeCode}</td>
                      <td>{room.placeName}</td>
                      <td>{room.placeNumber}</td>
                      <td>{room.priceCode}</td>
                      <td>{formatWeeklyPrice(room.priceCode)}</td>
                      <td>{room.status}</td>
                      <td>{room.accommodationType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          )}

          {/* Apartment Section */}
          {filteredInventory.Apartment.length > 0 && (
          <div className="inventory-section">
            <h2>Apartment</h2>
            <button onClick={() => addAccommodation('Apartment')}>Add Apartment</button>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Place Code</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Price Code</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.Apartment.map(room => (
                    <tr 
                      key={room.id}
                      onClick={() => setSelectedAccommodation(room)}
                      className="clickable-row"
                    >
                      <td>{room.placeCode}</td>
                      <td>{room.placeName}</td>
                      <td>{room.placeNumber}</td>
                      <td>{room.priceCode}</td>
                      <td>{formatWeeklyPrice(room.priceCode)}</td>
                      <td>{room.status}</td>
                      <td>{room.accommodationType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          )}

          {/* Dorm A Section */}
          {filteredInventory.dormA.length > 0 && (
          <div className="inventory-section">
            <h2>Dorm A</h2>
            <button onClick={() => addAccommodation('dormA')}>Add Dorm A Room</button>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Place Code</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Price Code</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.dormA.map(room => (
                    <tr 
                      key={room.id}
                      onClick={() => setSelectedAccommodation(room)}
                      className="clickable-row"
                    >
                      <td>{room.placeCode}</td>
                      <td>{room.placeName}</td>
                      <td>{room.placeNumber}</td>
                      <td>{room.priceCode}</td>
                      <td>{formatWeeklyPrice(room.priceCode)}</td>
                      <td>{room.status}</td>
                      <td>{room.accommodationType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          )}

          {/* Dorm B Section */}
          {filteredInventory.dormB.length > 0 && (
          <div className="inventory-section">
            <h2>Dorm B</h2>
            <button onClick={() => addAccommodation('dormB')}>Add Dorm B Room</button>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Place Code</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Price Code</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.dormB.map(room => (
                    <tr 
                      key={room.id}
                      onClick={() => setSelectedAccommodation(room)}
                      className="clickable-row"
                    >
                      <td>{room.placeCode}</td>
                      <td>{room.placeName}</td>
                      <td>{room.placeNumber}</td>
                      <td>{room.priceCode}</td>
                      <td>{formatWeeklyPrice(room.priceCode)}</td>
                      <td>{room.status}</td>
                      <td>{room.accommodationType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          )}

          {/* Fireside Section */}
          {filteredInventory.Fireside.length > 0 && (
          <div className="inventory-section">
            <h2>Fireside</h2>
            <button onClick={() => addAccommodation('Fireside')}>Add Fireside Room</button>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Place Code</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Price Code</th>
                    <th>Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.Fireside.map(room => (
                    <tr 
                      key={room.id}
                      onClick={() => setSelectedAccommodation(room)}
                      className="clickable-row"
                    >
                      <td>{room.placeCode}</td>
                      <td>{room.placeName}</td>
                      <td>{room.placeNumber}</td>
                      <td>{room.priceCode}</td>
                      <td>{formatWeeklyPrice(room.priceCode)}</td>
                      <td>{room.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          )}

          {/* Lower Court Section */}
          {filteredInventory.LowerCourt.length > 0 && (
          <div className="inventory-section">
            <h2>Lower Court</h2>
            <button onClick={() => addAccommodation('LowerCourt')}>Add Lower Court Room</button>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Place Code</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Price Code</th>
                    <th>Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.LowerCourt.map(room => (
                    <tr 
                      key={room.id}
                      onClick={() => setSelectedAccommodation(room)}
                      className="clickable-row"
                    >
                      <td>{room.placeCode}</td>
                      <td>{room.placeName}</td>
                      <td>{room.placeNumber}</td>
                      <td>{room.priceCode}</td>
                      <td>{formatWeeklyPrice(room.priceCode)}</td>
                      <td>{room.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          )}

          {/* Upper Court Section */}
          {filteredInventory.UpperCourt.length > 0 && (
          <div className="inventory-section">
            <h2>Upper Court</h2>
            <button onClick={() => addAccommodation('UpperCourt')}>Add Upper Court Room</button>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Place Code</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Price Code</th>
                    <th>Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.UpperCourt.map(room => (
                    <tr 
                      key={room.id}
                      onClick={() => setSelectedAccommodation(room)}
                      className="clickable-row"
                    >
                      <td>{room.placeCode}</td>
                      <td>{room.placeName}</td>
                      <td>{room.placeNumber}</td>
                      <td>{room.priceCode}</td>
                      <td>{formatWeeklyPrice(room.priceCode)}</td>
                      <td>{room.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          )}

          {/* Private Cottage Section */}
          {filteredInventory.privateCottage.length > 0 && (
          <div className="inventory-section">
            <h2>Private Cottages</h2>
            <button onClick={() => addAccommodation('privateCottage')}>Add Private Cottage</button>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Place Code</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Price Code</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.privateCottage.map(room => (
                    <tr 
                      key={room.id}
                      onClick={() => setSelectedAccommodation(room)}
                      className="clickable-row"
                    >
                      <td>{room.placeCode}</td>
                      <td>{room.placeName}</td>
                      <td>{room.placeNumber}</td>
                      <td>{room.priceCode}</td>
                      <td>{formatWeeklyPrice(room.priceCode)}</td>
                      <td>{room.status}</td>
                      <td>{room.accommodationType}</td>
                      <td>{room.privateCottageInfo?.ownerName || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          )}
        </div>
      )}

      {/* Facilities Modal */}
      {selectedAccommodation && (
        <FacilitiesModal
          isOpen={showFacilitiesModal}
          onClose={() => setShowFacilitiesModal(false)}
          onSave={handleSaveFacilitiesData}
          initialData={selectedAccommodation.facilitiesData}
          placeName={selectedAccommodation.placeName}
          placeCode={selectedAccommodation.placeCode}
        />
      )}
    </div>
  );
};

export default InventoryManagement; 